/**
 * 「我的作品库」内嵌的「我的导演项目」section
 *
 * - 三个子筛选：进行中 / 已完成 / 已归档
 * - 每个筛选下用 ProjectCard 展示前 6 个，超出显示「查看全部」跳到 /director
 * - 已归档卡片提供「恢复」按钮
 *
 * 注：之前位于「用户中心」（spec Requirement 12 旧版位置），现移到 /works 作品库
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Clapperboard } from 'lucide-react';
import toast from '../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorProjectWithCover,
} from '../services/directorApi';
import { ProjectCard } from './ProjectCard';

type Filter = 'active' | 'completed' | 'archived';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'active', label: '进行中' },
  { key: 'completed', label: '已完成' },
  { key: 'archived', label: '已归档' },
];

const PREVIEW_LIMIT = 6;

export function WorksDirectorSection() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('active');
  const [projects, setProjects] = useState<DirectorProjectWithCover[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const apiFilter = filter === 'archived' ? 'archived' : 'active';
      const result = await directorApi.listProjects({
        filter: apiFilter,
        page: 1,
        pageSize: PREVIEW_LIMIT * 4,
      });
      let data = result.data;
      if (filter === 'completed') {
        data = data.filter((p) => p.status === 'completed');
      } else if (filter === 'active') {
        data = data.filter((p) => p.status !== 'completed');
      }
      setProjects(data.slice(0, PREVIEW_LIMIT));
      setTotal(data.length);
    } catch (err) {
      console.warn('[WorksDirectorSection] 加载失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleUnarchive = async (projectId: string) => {
    try {
      await directorApi.unarchiveProject(projectId);
      toast.success('已恢复');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '恢复失败');
    }
  };

  return (
    <section className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg shadow-sky-200/70">
            <Clapperboard className="h-4 w-4" />
          </span>
          <h3 className="text-base font-semibold text-slate-900">我的导演项目</h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/apps/director')}
          className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 transition hover:text-sky-700"
        >
          查看全部
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* 子筛选 */}
      <div className="mb-4 flex gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
              filter === f.key
                ? 'bg-slate-950 text-white shadow-md'
                : 'bg-white text-slate-500 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
        </div>
      ) : projects.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          {filter === 'archived'
            ? '没有已归档项目'
            : filter === 'completed'
              ? '还没有完成的项目'
              : '还没有导演项目，点「查看全部」开始创作'}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onUnarchive={(id) => handleUnarchive(id)}
              />
            ))}
          </div>
          {total > PREVIEW_LIMIT && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => navigate('/apps/director')}
                className="text-xs font-semibold text-sky-600 transition hover:text-sky-700"
              >
                还有 {total - PREVIEW_LIMIT} 个项目，查看全部
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
